import { MarketingLayout, Hero, BenefitCards, Steps, Faq, FinalCta, Note } from "../components/MarketingLayout";
import { useSeo } from "../lib/useSeo";

export default function Psychiatrists() {
  useSeo({
    title: "SimplyRefer for Psychiatrists | GP Referral Directory",
    description:
      "Help GP practices find psychiatrists by clinical focus, availability and referral status. Join SimplyRefer's Australian mental health referral directory.",
    canonicalPath: "/psychiatrists",
  });

  return (
    <MarketingLayout active="/psychiatrists">
      <Hero
        eyebrow="For Psychiatrists"
        h1="Control your referral intake without opening the floodgates."
        sub="SimplyRefer lets psychiatrists signal whether they're open, waitlisted or closed, optionally share exact capacity, and find psychologists for patients who need ongoing therapy."
        primary={{ to: "/clinician/signup", label: "Join the directory" }}
        secondary={{ to: "/clinic/signup", label: "Register a clinic" }}
      />

      <BenefitCards items={[
        { title: "Show your current availability", desc: "Show whether your rooms are open, waitlisted or closed, so GPs can judge suitability before they send a referral." },
        { title: "Show clinical focus", desc: "Help GPs understand your diagnostic focus, subspecialty interests and suitable referral types." },
        { title: "List 291 assessments", desc: "Only have time for one-off assessments? List that separately from ongoing care, even while your books are closed.", highlight: true },
        { title: "Reduce admin overhead", desc: "When GPs can see intake status upfront, your rooms may spend less time managing unsuitable calls and emails." },
        { title: "See meaningful GP engagement", desc: "Optional Insights show search appearances, profile views, referral selections and unique GP practices without patient information." },
        { title: "No patient information stored", desc: "You continue using your normal referral process. SimplyRefer does not store patient names, clinical information or referral letters." },
      ]} />

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-5 pb-4 sm:px-8">
          <div className="rounded-xl border border-primary/30 bg-primary/[0.03] p-5 sm:p-6" data-testid="practitioner-pause-box">
            <p className="font-heading text-base font-semibold text-slate-900">Only pay when you're taking patients</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              If you sign up as an individual practitioner and manage your own calendar, your subscription pauses when your books are closed, so you only pay when you are looking to take on new patients.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              <li>• Simple monthly subscription only — no annual commitment.</li>
              <li>• While your books are closed you stay on the directory for free, shown as not currently accepting referrals.</li>
              <li>• First 3 months free, no card required, beginning only when SimplyRefer launches.</li>
            </ul>
          </div>
        </div>
      </section>

      <Steps steps={[
        "Create a psychiatrist profile.",
        "Add clinical focus areas, subspecialty interests and referral preferences.",
        "Keep intake status current, including open, waitlisted or closed.",
        "GP practices can check suitability before preparing a referral.",
        "See when a GP selects you through SimplyRefer, with optional references available when they need one.",
      ]} />

      <Note title="For clinics and practice managers">
        Clinic managers can manage multiple psychiatrist or clinician profiles, availability and referral status from one account, helping reduce repeated admin updates across the practice.
      </Note>

      <Faq items={[
        { q: "Can I show my subspecialty or clinical focus?", a: "Yes. Your profile can show your areas of clinical focus and referral preferences." },
        { q: "Can I show whether I am accepting new referrals?", a: "Yes. SimplyRefer is designed to show current referral status." },
        { q: "Does SimplyRefer store patient information?", a: "No. GPs continue writing referral letters through their normal process. SimplyRefer records the referring practice and referral option, not patient information." },
      ]} />

      <FinalCta
        heading="Join the SimplyRefer directory."
        primary={{ to: "/clinician/signup", label: "Join the directory" }}
        secondary={{ to: "/clinic/signup", label: "Register a clinic" }}
      />
    </MarketingLayout>
  );
}
